import React, { useMemo, useState } from "react";
import Form from "../../components/Form";
import { Controller, useForm } from "react-hook-form";
import { makeStyles } from "../../utils/styles";
import { yupResolver } from "@hookform/resolvers/yup";
import { FORM } from "../../utils/constans";
import * as yup from "yup";
import Paper from "../../components/Paper";
import Input from "../../components/Input";
import Divider from "../../components/Divider";
import Typography from "../../components/Typography";
import ButtonGroup from "../../components/ButtonGroup";
import Button from "../../components/Button";
import Icon from "../../components/Icon";
import PropTypes from "prop-types";
import { useTheme } from "styled-components";
import { Col, Row } from "react-styled-flexboxgrid";
import IconButton from "../../components/IconButton";
import Select from "../../components/Select";
import { generatePassword, parseDataToOptions } from "../../utils/helpers";
import Loader from "../../components/Loader";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles((css) => ({
    iconButton: css`
        margin: 0 10px;
    `,
    paper: css`
        position: relative;
        overflow: hidden;
        padding: 40px 40px 15px 40px;
        width: 680px;
    `,
    title: css`
        margin-bottom: 35px;
    `,
    divider: css`
        margin: 25px 0;
    `,
    dividerFooter: css`
        margin: 5px 0 15px 0;
    `,
    buttons: css`
        display: flex;
        justify-content: flex-end;
    `,
    marginBottom: css`
      margin-bottom: 5px;
  `,
}));

const schemaBasic = {
    first_name: yup.string().required(FORM.FIELD_IS_REQUIRED),
    last_name: yup.string().required(FORM.FIELD_IS_REQUIRED),
    role_id: yup.string().required(FORM.FIELD_IS_REQUIRED),
    email: yup.string().email(FORM.EMAIL_IS_INCORRECT).required(FORM.FIELD_IS_REQUIRED)
};

const schemaAdd = yup.object().shape({
    ...schemaBasic,
    password: yup.string().required(FORM.FIELD_IS_REQUIRED).min(6).max(20),
});

const schemaEdit = yup.object().shape({
    ...schemaBasic
});

const getPreloadedValues = () => ({
    role_id: ''
});

const FormUser = ({ loading, edit, initialValues, roles, onSubmit, onClickCancel }) => {
    const { t } = useTranslation()
    const styles = useStyles();
    const theme = useTheme();

    const [passwordShow, setPasswordShow] = useState(false);

    const { register, handleSubmit, errors, setValue, control } = useForm({
        resolver: yupResolver(edit ? schemaEdit : schemaAdd),
        defaultValues: {
            ...getPreloadedValues(),
            ...initialValues
        },
    });

    // Memoize data
    const rolesOptions = useMemo(
        () => roles && parseDataToOptions(roles),
        [roles]
    );

    const handleTogglePassword = () => setPasswordShow((prevState) => !prevState);

    const handleClickGeneratePassword = () => setValue('password', generatePassword());

    return (
        <Paper customStyle={styles.paper}>
            <Form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
                <Typography customStyle={styles.title} as="h1">
                    {edit ? t("Edytuj użytkownika") : t("Dodaj użytkownika")}
                </Typography>
                <Divider customStyle={styles.divider} />
                <Row>
                    <Col xs={6}>
                        <Input
                            type="text"
                            placeholder={t("Imię")}
                            ref={register}
                            name="first_name"
                            label={t("Imię")}
                            error={!!errors.first_name}
                            errorText={errors.first_name?.message}
                        />
                    </Col>
                    <Col xs={6}>
                        <Input
                            type="text"
                            placeholder={t("Nazwisko")}
                            ref={register}
                            name="last_name"
                            label={t("Nazwisko")}
                            error={!!errors.last_name}
                            errorText={errors.last_name?.message}
                        />
                    </Col>
                </Row>
                <Row>
                    <Col xs={6}>
                        <Controller
                            control={control}
                            name="role_id"
                            render={({ onChange, value }) => (
                                <Select
                                    value={value}
                                    onChange={onChange}
                                    label={t("Uprawnienia")}
                                    placeholder={t("Wybierz")}
                                    options={rolesOptions}
                                    error={!!errors.role_id}
                                    errorText={errors.role_id?.message}
                                    fullWidth
                                    customStyle={styles.marginBottom}
                                />
                            )}
                        />
                    </Col>
                </Row>
                <Divider customStyle={styles.divider} />
                <Input
                    type="text"
                    ref={register}
                    name="email"
                    label={t("Adres email")}
                    placeholder={t("Email")}
                    autoComplete="email"
                    error={!!errors.email}
                    errorText={errors.email?.message}
                />
                <Input
                    endAdornment={
                        <>
                            <Divider orientation="vertical" offset={20} />
                            <IconButton onClick={handleTogglePassword} customStyle={styles.iconButton}>
                                <Icon name="eye" />
                            </IconButton>
                        </>
                    }
                    type={passwordShow ? "text" : "password"}
                    ref={register}
                    name="password"
                    label={t("Hasło")}
                    placeholder={t("Hasło")}
                    autoComplete="new-password"
                    error={!!errors.password}
                    errorText={errors.password?.message}
                    customStyle={styles.marginBottom}
                />
                <Button
                    variant="text"
                    startIcon={
                        <Icon name="lock" width={12} height={14} fill={theme.colors.purple} />
                    }
                    onClick={handleClickGeneratePassword}
                >
                    {edit ? t("GENERUJ NOWE HASŁO") : t("GENERUJ HASŁO")}
                </Button>
                <Divider customStyle={styles.dividerFooter} />
                <ButtonGroup customStyle={styles.buttons}>
                    <Button onClick={onClickCancel} variant="text" startIcon={<Icon name="close" width={12} height={12} />}>
                        {t("ANULUJ")}
                    </Button>
                    <Button
                        type="submit"
                        startIcon={!edit ?
                            <Icon name="add" width={12} height={12} fill={theme.colors.white} /> :
                            <Icon name="save" width={14} height={14} fill={theme.colors.white} />
                        }
                        disabled={loading}
                    >
                        {edit ? t("ZAPISZ") : t("DODAJ UŻYTKOWNIKA")}
                    </Button>
                </ButtonGroup>
            </Form>
            <Loader show={loading} />
        </Paper >
    );
};

FormUser.propTypes = {
    loading: PropTypes.bool,
    edit: PropTypes.bool,
    initialValues: PropTypes.object,
    departments: PropTypes.array,
    onSubmit: PropTypes.func,
    onClickCancel: PropTypes.func,
};

export default FormUser;
